import { createContext, useContext } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Menu,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
  MenuSub,
  MenuSubTrigger,
  MenuTrigger,
  Button,
  IconButton,
  Tooltip,
} from '@/shared/ui';
import { ArrowRightLeft, Bell, BellOff, MoreHorizontal } from 'lucide-react';
import { TaskIcon, ticketStatusTone, type TicketStatusTone } from '@/shared/config';
import { useMediaQuery } from '@/shared/lib/useMediaQuery';
import { cn } from '@/shared/lib/cn';
import {
  ACTION_GROUP_LABELS,
  ACTION_GROUP_ORDER,
  useTicketActions,
  type ActionGroup,
  type HeldTask,
  type TicketAction,
  type UseTicketActionsArgs,
} from './manage/useTicketActions';
import styles from './TicketActions.module.css';

const ActionsContext = createContext<{ actions: TicketAction[]; heldByTasks: HeldTask[] } | null>(null);

export const TicketActionsProvider: React.FC<UseTicketActionsArgs & { children: React.ReactNode }> = ({
  children,
  ...args
}) => {
  const { actions, dialogs, heldByTasks } = useTicketActions(args);
  return (
    <ActionsContext.Provider value={{ actions, heldByTasks }}>
      { }
      {heldByTasks.length > 0 && <HeldByTasksCallout tasks={heldByTasks} />}
      {children}
      {dialogs}
    </ActionsContext.Provider>
  );
};

const HeldByTasksCallout: React.FC<{ tasks: HeldTask[] }> = ({ tasks }) => (
  <Card
    role="status"
    className={styles.heldCallout}
    style={
      {
        '--nested-bg': 'color-mix(in oklab, var(--tone-pending) 14%, var(--surface-raised))',
        '--nested-radius': 'var(--radius-4xl)',
      } as React.CSSProperties
    }
  >
    <div className={styles.heldHead}>
      <span className={styles.heldIcon} aria-hidden>
        <TaskIcon size={16} />
      </span>
      <p className={styles.heldText}>
        <strong>Заявку держит разработка</strong> —{' '}
        {tasks.length === 1 ? 'незавершённая задача' : `незавершённых задач: ${tasks.length}`}
      </p>
    </div>
    <ul className={styles.heldList}>
      {tasks.map((t) => (
        <li key={t.id} className={styles.heldItem}>
          <Link to={`/sprints?task=${t.id}`} className={styles.heldLink} title={t.title}>
            {t.key} · {t.title}
          </Link>
          <span className={styles.heldStatus}>{t.statusLabel}</span>
        </li>
      ))}
    </ul>
  </Card>
);

export const useTicketActionsContext = () => useContext(ActionsContext);

export const TicketActionsBar: React.FC = () => {
  const ctx = useTicketActionsContext();
  return ctx ? <TicketActionsView actions={ctx.actions} /> : null;
};

const WIDE = '(min-width: 961px)';

const STATUS_DOT_TONE: Record<TicketStatusTone, string> = {
  neutral: styles.dotNeutral,
  pending: styles.dotPending,
  info: styles.dotInfo,
  accent: styles.dotAccent,
  review: styles.dotReview,
  warning: styles.dotWarning,
  positive: styles.dotPositive,
  danger: styles.dotDanger,
};

const TicketActionsView: React.FC<{ actions: TicketAction[] }> = ({ actions }) => {
  const wide = useMediaQuery(WIDE);
  const slots = wide ? 3 : 1;

  const shown = actions.filter((a) => a.bar).slice(0, slots);
  const shownKeys = new Set(shown.map((a) => a.key));
  const mute = actions.find((a) => a.key === 'mute');
  const rest = actions.filter((a) => !shownKeys.has(a.key) && a.key !== 'mute');

  const dangerous = rest.filter((a) => a.danger);
  const byGroup = ACTION_GROUP_ORDER.map((group) => ({
    group,
    items: rest.filter((a) => a.group === group && !a.danger),
  })).filter((g) => g.items.length > 0);

  return (
    <div className={styles.bar}>
      {shown.map((a, i) => (
        <Button
          key={a.key}
          size="sm"
          variant={i === 0 && wide ? 'primary' : 'outline'}
          className={styles.barBtn}
          onClick={a.run}
          disabled={a.disabled}
          aria-disabled={a.ariaDisabled || undefined}
          tooltip={a.hint}
          tooltipSide="bottom"
        >
          <span className={styles.barBtnLabel}>{a.label}</span>
        </Button>
      ))}

      {mute && (
        <IconButton
          label={mute.label}
          tooltip={mute.hint}
          tooltipSide="bottom"
          className={cn(styles.kebab, mute.active && styles.muteOn)}
          onClick={mute.run}
          disabled={mute.disabled}
          aria-pressed={mute.active}
        >
          {mute.active ? <BellOff size={16} aria-hidden /> : <Bell size={16} aria-hidden />}
        </IconButton>
      )}

      <Menu>
        <MenuTrigger
          render={<IconButton label="Действия по заявке" className={styles.kebab} tooltipSide="bottom" />}
        >
          <MoreHorizontal size={16} aria-hidden />
        </MenuTrigger>
        <MenuContent>
          {byGroup.map(({ group, items }) => {
            const subKey = group === 'status' ? 'transition:' : group === 'service' ? 'complexity:' : null;
            const sub = subKey ? items.filter((a) => a.key.startsWith(subKey)) : [];
            const plain = subKey ? items.filter((a) => !a.key.startsWith(subKey)) : items;
            const subLabel = group === 'status' ? 'Перевести в статус' : 'Сложность';
            return (
              <MenuGroup key={group}>
                {label(group) && <MenuGroupLabel>{label(group)}</MenuGroupLabel>}
                {sub.length > 0 && (
                  <MenuSub>
                    <MenuSubTrigger>
                      <span className={styles.subLead}>
                        {group === 'service' ? sub[0].icon : <ArrowRightLeft size={14} aria-hidden />}
                        {subLabel}
                      </span>
                    </MenuSubTrigger>
                    <MenuContent>
                      {sub.map((a) => {
                        const item = (
                          <MenuItem
                            key={a.key}
                            disabled={a.disabled}
                            aria-disabled={a.ariaDisabled || undefined}
                            className={cn(a.ariaDisabled && styles.itemBlocked)}
                            onClick={a.run}
                          >
                            { }
                            {group === 'status' && (
                              <span
                                className={cn(styles.statusDot, STATUS_DOT_TONE[ticketStatusTone(a.key.slice(subKey!.length))])}
                                aria-hidden
                              />
                            )}
                            {group === 'service' ? a.label.replace(/^(Запросить сложность|Сложность): /, '') : a.label}
                          </MenuItem>
                        );
                        return a.ariaDisabled && a.hint ? (
                          <Tooltip key={a.key} content={a.hint} side="left">
                            {item}
                          </Tooltip>
                        ) : (
                          item
                        );
                      })}
                    </MenuContent>
                  </MenuSub>
                )}
                {plain.map((a) => (
                  <MenuItem key={a.key} disabled={a.disabled} onClick={a.run}>
                    { }
                    {a.icon ?? <span className={styles.iconSlot} aria-hidden />}
                    {a.label}
                  </MenuItem>
                ))}
              </MenuGroup>
            );
          })}
          {dangerous.length > 0 && <MenuSeparator />}
          {dangerous.map((a) => (
            <MenuItem
              key={a.key}
              className={cn(styles.danger)}
              disabled={a.disabled}
              onClick={a.run}
            >
              {a.icon ?? <span className={styles.iconSlot} aria-hidden />}
              {a.label}
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>
    </div>
  );
};

function label(group: ActionGroup): string | null {
  return ACTION_GROUP_LABELS[group];
}
